import { useEffect } from 'react';

/** Lifts the app shell's height and overflow lock. Declared in `index.css`. */
const DOCUMENT_SCROLL_CLASS = 'af-document-scroll';

/**
 * Hands scrolling back to the document for as long as the flow is on screen.
 *
 * The app shell pins html, body and `#root` to the window and scrolls inside a div. That is
 * right for a logged-in application and the thing mobile browsers cope with worst: their
 * toolbars, and the viewport those toolbars leave behind, are built around a document that
 * scrolls. These routes own the whole screen and nest inside no layout, so they opt out, and
 * the lock is restored on unmount.
 *
 * This is also what makes the flow recoverable when Chrome for iOS opens it from a mail client
 * painted a toolbar's height too high: as an ordinary page, one scroll puts it right for good.
 * The offset itself is a browser bug and not fixable from here — measurements and the six
 * attempts that failed are in `.temp/apply-flow-backend-followups.md`.
 *
 * It is a known OS fault, and not ours alone to suffer. iOS 26 introduced a family of viewport
 * faults around the browser's own toolbars — pages drawn offset, blank gaps, fixed and sticky
 * elements misplaced, all while the values a page can read stay normal — hitting Safari and
 * Chrome for iOS alike, since both run WebKit. Apple fixed one variant in Safari 26.1 and others
 * are still open, so retest after an iOS release before spending any time here:
 *
 *   https://developer.apple.com/forums/thread/800125  (fixed/sticky misaligned after iOS 26)
 *   https://developer.apple.com/forums/thread/801028  (sticky elements vs. the hiding toolbar)
 *   https://github.com/mui/material-ui/issues/46953   (the same bottom gap, another library)
 *
 * Used by the boot loader as well as the stage, because the first moments of the load belong
 * to the loader and the class has to be on <html> for them too.
 */
export const useDocumentScroll = () => {
  useEffect(() => {
    const root = document.documentElement;

    root.classList.add(DOCUMENT_SCROLL_CLASS);

    return () => root.classList.remove(DOCUMENT_SCROLL_CLASS);
  }, []);
};
